import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { adminQuickGrant, overrideUserSpec, type QuickGrantKind } from '@/client/admin.client';
import { pickUserSpec, validateUserSpec } from '@rb/shared/user-state/user-spec';
import type { UserGlobalState } from '@rb/shared/user-state/types';

const QUICK_GRANTS: { kind: QuickGrantKind; label: string }[] = [
  { kind: 'upgrades', label: '모든 업그레이드 max' },
  { kind: 'skins', label: '모든 스킨 언락' },
  { kind: 'pets', label: '모든 펫 획득·만렙' },
  { kind: 'breakthrough', label: '돌파 최대' },
];

interface Props {
  account: string;
  state: Record<string, unknown> | null;
  /** dev/staging verse 에서만 true — prod 는 Import 섹션 자체를 숨긴다. */
  canImport: boolean;
  onApplied: () => void;
}

/**
 * 유저 스펙(캐릭터·업그레이드·펫·스킨·장착) Export/Import 패널.
 * Export 는 전 env read-only. Import 는 dev/staging 전용(canImport)이며 서버도
 * prod 를 차단한다. 부모는 `key={account}` 로 마운트해 유저 전환 시 초기화한다.
 */
export function UserSpecTransferPanel({ account, state, canImport, onApplied }: Props) {
  const specJson = useMemo(
    () => JSON.stringify(pickUserSpec(state as UserGlobalState | null), null, 2),
    [state]
  );

  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [grantKind, setGrantKind] = useState<QuickGrantKind | null>(null);
  const [grantMsg, setGrantMsg] = useState<string | null>(null);
  const [grantError, setGrantError] = useState<string | null>(null);

  const handleQuickGrant = async (kind: QuickGrantKind, label: string) => {
    setGrantMsg(null);
    setGrantError(null);
    if (!window.confirm(`${account} 에게 "${label}" 을(를) 부여합니다. 계속할까요?`)) return;
    setGrantKind(kind);
    try {
      const r = await adminQuickGrant(account, kind);
      setGrantMsg(`${label}: ${r.count}건 부여됨`);
      onApplied();
    } catch (e) {
      setGrantError(e instanceof Error ? e.message : String(e));
    } finally {
      setGrantKind(null);
    }
  };

  const handleCopy = async () => {
    setCopyMsg(null);
    try {
      await navigator.clipboard.writeText(specJson);
      setCopyMsg('복사됨');
    } catch {
      setCopyMsg('클립보드 권한 없음 — 미리보기에서 수동 복사하세요');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([specJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-spec-${account}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    setImportError(null);
    setImportMsg(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(importText);
    } catch (e) {
      setImportError(`JSON 파싱 실패: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    const validated = validateUserSpec(parsed);
    if (!validated.ok) {
      setImportError(validated.error);
      return;
    }
    // 전체 교체는 파괴적 — 붙여넣기가 잘리면 캐릭터/펫/슬롯이 통째로 리셋된다.
    if (!window.confirm(`${account} 의 스펙을 전체 교체합니다. 계속할까요?`)) return;
    setBusy(true);
    try {
      const r = await overrideUserSpec(account, validated.spec);
      setImportMsg(`적용됨: ${r.applied.join(', ')}`);
      onApplied();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-md border border-sky-500/40 bg-sky-500/5 p-3">
      <div className="flex items-center gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Spec transfer
        </h4>
        <Badge variant="outline" className="border-sky-500/60 text-sky-600">
          캐릭터·업그레이드·펫·스킨
        </Badge>
      </div>

      {/* Export */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" onClick={handleCopy}>
            JSON 복사
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleDownload}>
            JSON 다운로드
          </Button>
          {copyMsg && <span className="text-xs text-muted-foreground">{copyMsg}</span>}
        </div>
        <pre className="max-h-48 overflow-auto rounded-md bg-muted/50 p-2 text-xs font-mono">
          {specJson}
        </pre>
      </div>

      {canImport && (
        <>
          <Separator />
          {/* Import — dev/staging only */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">스펙 JSON 붙여넣기 → 전체 교체</span>
              <Badge variant="outline" className="border-amber-500/60 text-amber-600">
                Dev/Staging only
              </Badge>
            </div>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              disabled={busy}
              placeholder='{ "characters": [...], "pets": [...], ... }'
              className="h-32 w-full rounded-md border border-input bg-background p-2 text-xs font-mono"
            />
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" onClick={handleImport} disabled={busy || !importText}>
                {busy ? '…' : '스펙 적용'}
              </Button>
              {importMsg && <span className="text-xs text-emerald-600">{importMsg}</span>}
            </div>
            {importError && <p className="text-xs text-destructive">{importError}</p>}
          </div>

          <Separator />
          {/* Quick grant — d.unlockAll() 의 서버측 등가물, kind 단위 부여 (dev/staging only) */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Quick grant — 전체 부여</span>
              <Badge variant="outline" className="border-amber-500/60 text-amber-600">
                Dev/Staging only
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {QUICK_GRANTS.map(({ kind, label }) => (
                <Button
                  key={kind}
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={grantKind !== null}
                  onClick={() => void handleQuickGrant(kind, label)}
                >
                  {grantKind === kind ? '…' : label}
                </Button>
              ))}
            </div>
            {grantMsg && <span className="text-xs text-emerald-600">{grantMsg}</span>}
            {grantError && <p className="text-xs text-destructive">{grantError}</p>}
          </div>
        </>
      )}
    </div>
  );
}

export default UserSpecTransferPanel;
